import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import Calendar from 'components/shared/Calendar'
import { ModalHeader, ModalFooter, ModalCalendar } from './styles'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { setDates, setMultipleDays } from 'store/ticket-slice'
import { formatDates } from 'helpers/dates'
import { useResolutions } from 'store/resolutions'
import { FormattedMessage } from 'react-intl'
import moment, { Moment } from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useListTicketAvailableDaysQuery } from 'store/api-slice/hooks'
import { useTickets } from 'store/ticket-slice/hooks'
import { closeModalActions, pushDataLayer } from 'helpers/helpers'
import { CalendarLoader } from 'components/shared/Calendar/components/CalendarLoader'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import CalendarModalLegend from './components/CalendarModalLegend'

interface CalendarProps {
    isOpened: boolean
    close: () => void
    openHoursModal: () => void
}

const CalendarModal = ({ isOpened, close, openHoursModal }: CalendarProps) => {
    const dispatch = useAppDispatch()
    const { language, isCreatorAccommodation } = useApp()
    const {
        isDarkThemeForTickets,
        isBlackPromoForTickets,
        isBirthdayPromoForTickets,
        isSummerSaleForTickets,
    } = usePromotions()
    const { dates, isMultipleDays, areTicketsVisible } = useAppSelector(
        (state) => state.ticket
    )
    const [isDisabledCalendar, setIsDisabledCalendar] = useState(false)
    const [isConfirmed, setIsConfirmed] = useState(false)
    const [unconfirmedDates, setUnconfirmedDates] = useState<string[]>(
        dates || []
    )
    const { isMobile } = useResolutions()
    const { data, isFetching } = useListTicketAvailableDaysQuery(
        {
            _locale: language,
        },
        { skip: !isOpened }
    )
    const { removeAllTicketsFromCart } = useTickets()

    const isEventsLegendVisible = useMemo(() => {
        if (!data?.events?.length) return false
        // has to have some events with legend item definition
        return data.events.some(
            ({ iconColor, legendDescription }) => iconColor && legendDescription
        )
    }, [data])
    const isHolidaysLegendVisible = !isEventsLegendVisible

    const handleSetDates = (dates: Moment[]) => {
        if (dates.length > 1) {
            const fullDatesRange = []

            for (
                let date = moment(dates[0]);
                date.isSameOrBefore(dates[1]);
                date.add(1, 'days')
            ) {
                fullDatesRange.push(date.format('YYYY-MM-DD'))
            }
            setUnconfirmedDates(fullDatesRange)
        } else {
            setUnconfirmedDates([dates[0].format('YYYY-MM-DD')])
        }
        if (!isMobile) {
            setIsConfirmed(true)
        }
    }

    const confirmDates = () => {
        if (dates !== unconfirmedDates && areTicketsVisible)
            removeAllTicketsFromCart()
        if (
            unconfirmedDates[0] ===
            unconfirmedDates[unconfirmedDates.length - 1]
        ) {
            dispatch(setMultipleDays(false))
        }
        dispatch(setDates(unconfirmedDates))

        const closeModal = () => {
            setIsConfirmed(false)
            setIsDisabledCalendar(false)
            close()
            clearTimeout(timeout)
            closeModalActions(false)
        }

        const timeout = setTimeout(closeModal, 500)

        // < analytics
        !isCreatorAccommodation &&
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'tickets',
                event_action: 'arrival_date_selected',
                event_label: '',
            })
        // analytics >
    }

    useEffect(() => {
        if (isConfirmed) {
            setIsDisabledCalendar(true)
            confirmDates()
        }
        return () => {
            setIsDisabledCalendar(false)
        }
    }, [isConfirmed])

    useEffect(() => {
        if (isFetching || !data) return
        setIsDisabledCalendar(false)
    }, [isFetching])

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={786}
            onClose={() => {
                close()
                setUnconfirmedDates([])
            }}
            isMobileFullscreen={true}
            testIdPrefix="ticket-calendar"
        >
            <>
                <ModalHeader hasDarkMode={isDarkThemeForTickets} isFullWidth>
                    <Text
                        variant="body"
                        mb={4}
                        color={
                            isDarkThemeForTickets
                                ? 'grayscale_500'
                                : 'grayscale_900'
                        }
                    >
                        <FormattedMessage
                            id="global.calendar.selected_date_label"
                            defaultMessage="Data pobytu"
                        />
                    </Text>

                    {dates.length > 0 ? (
                        <Box direction="row" gap={4}>
                            <Text
                                variant="bodyLarge"
                                color={
                                    isDarkThemeForTickets
                                        ? 'grayscale_300'
                                        : 'secondary_500'
                                }
                            >
                                {formatDates(dates, true)}
                            </Text>
                            {dates.length > 1 && (
                                <Text variant="bodyLarge" color="grayscale_700">
                                    ({dates.length}{' '}
                                    <FormattedMessage
                                        id="global.days.label_plural"
                                        defaultMessage="dni"
                                    />
                                    )
                                </Text>
                            )}
                        </Box>
                    ) : (
                        <Text
                            variant="bodyLarge"
                            color={
                                isDarkThemeForTickets
                                    ? 'grayscale_300'
                                    : 'secondary_500'
                            }
                        >
                            <FormattedMessage
                                id="global.calendar.selected_date_placeholder"
                                defaultMessage="Wybierz dzień przyjazdu"
                            />
                        </Text>
                    )}
                </ModalHeader>
                {isFetching ? (
                    <CalendarLoader hasDarkMode={isDarkThemeForTickets} />
                ) : (
                    <>
                        <ModalCalendar hasDarkMode={isDarkThemeForTickets}>
                            <Calendar
                                mode={isMultipleDays ? 'range' : 'single'}
                                handleDatesChosen={handleSetDates}
                                datesInfo={data ? data : null}
                                isLimitedRange={true}
                                isDisabled={isDisabledCalendar}
                                date={
                                    !isMultipleDays && dates.length > 0
                                        ? moment(dates[0])
                                        : null
                                }
                                rangeDateStart={
                                    isMultipleDays && dates.length > 0
                                        ? moment(dates[0])
                                        : null
                                }
                                rangeDateEnd={
                                    isMultipleDays && dates.length > 0
                                        ? moment(dates[dates.length - 1])
                                        : null
                                }
                                testIdPrefix="ticket"
                                hasDarkMode={isDarkThemeForTickets}
                                hasAdditionalInfo={
                                    isBlackPromoForTickets ||
                                    isBirthdayPromoForTickets ||
                                    isSummerSaleForTickets
                                }
                            />
                        </ModalCalendar>
                        <ModalFooter
                            gap={16}
                            align="center"
                            hasDarkMode={isDarkThemeForTickets}
                            isFullWidth
                        >
                            <CalendarModalLegend
                                isEventsLegendVisible={isEventsLegendVisible}
                                isHolidaysLegendVisible={
                                    isHolidaysLegendVisible
                                }
                                eventsData={data?.events}
                                testIdPrefix="ticket"
                                openHoursModal={openHoursModal}
                            />

                            {isMobile && (
                                <Box mt={24} isFullWidth>
                                    <Button
                                        onClick={confirmDates}
                                        isDisabled={
                                            unconfirmedDates.length === 0
                                        }
                                    >
                                        <FormattedMessage
                                            id="global.btn.confirm"
                                            defaultMessage="Potwierdź"
                                        />
                                    </Button>
                                </Box>
                            )}
                        </ModalFooter>
                    </>
                )}
            </>
        </Modal>
    )
}

export default CalendarModal
